import { useState, useEffect, useRef } from 'react';
import { BarChart, GitBranch, Building2, Map, FileText, AlertTriangle, Info, ClipboardList, Layers, ExternalLink, FileSearch, Shield, LockKeyhole, MessageSquare } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useProject } from '@/contexts/ProjectContext';
import { useProjectSync } from '@/hooks/useProjectSync';
import { formatProjectVersionState, lockedWriteBackMessage } from '@/lib/projectVersionState';
import ConnectModal from '@/components/threatcat/ConnectModal';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OverviewTab from '../tabs/OverviewTab';
import ConnectionsTab from '../tabs/ConnectionsTab';
import EntitiesTab from '../tabs/EntitiesTab';
import BoundariesTab from '../tabs/BoundariesTab';
import NotesTab from '../tabs/NotesTab';
import ProjectInfoTab from '../tabs/ProjectInfoTab';
import ThreatsTab from '../tabs/ThreatsTab';
import DiagramTab from '../tabs/DiagramTab';
import QuestionsTab from '../tabs/QuestionsTab';
import DiagramsTab from '../tabs/DiagramsTab';
import ReconTab from '../tabs/ReconTab';
import StrideTab from '../tabs/StrideTab';
import ReviewTab from '../tabs/ReviewTab';
import { toast } from 'sonner';

const THREATCAT_URL = import.meta.env.VITE_THREATCAT_URL || 'https://threatcat.i.mercedes-benz.com';

const TRIGGER =
  'rounded-none px-3 sm:px-4 py-3 text-sm gap-1.5 sm:gap-2 border-b-2 border-transparent -mb-px ' +
  'data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary ' +
  'text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap';

export default function MainLayout() {
  const {
    isProject,
    projectInfo,
    diagrams,
    threats,
    reviewComments = [],
    fileName,
    activeTab,
    setActiveTab,
  } = useApp();
  const { questions, quickScanResults } = useQuestions();
  const { cookie, isConnected, disconnect } = useThreatCat();
  const { projectId, detail, syncState, syncError } = useProject();
  const { syncProject } = useProjectSync();
  const [connectOpen, setConnectOpen] = useState(false);
  const [pendingManualSync, setPendingManualSync] = useState<{ projectId: number; projectVersionId: number } | null>(null);

  // Build ThreatCat URL from the synchronized local project metadata.
  const threatCatUrl = (() => {
    if (!projectId || !detail?.project_version_id) return null;
    return `${THREATCAT_URL}/projects/${projectId}/versions/${detail.project_version_id}/overview`;
  })();

  const synchronizeNow = () => {
    if (!projectId || !detail?.project_version_id) return;
    const sync = { projectId, projectVersionId: detail.project_version_id };
    if (!cookie) {
      setPendingManualSync(sync);
      setConnectOpen(true);
      return;
    }
    void syncProject(sync);
  };

  const handleConnected = async () => {
    const sync = pendingManualSync;
    setPendingManualSync(null);
    if (sync) await syncProject(sync);
  };

  const handleConnectClose = () => {
    setConnectOpen(false);
    setPendingManualSync(null);
  };

  const [questionsReady, setQuestionsReady] = useState(false);
  const [diagramView, setDiagramView] = useState<'list' | 'detail'>('list');
  const [diagramSubTab, setDiagramSubTab] = useState('overview');
  const prevQuestionsLengthRef = useRef(0);
  const prevAnalysisKeysRef = useRef(0);

  // Pulse the Questions tab when questions are generated from another tab
  useEffect(() => {
    if (questions.length > 0 && prevQuestionsLengthRef.current === 0 && activeTab !== 'questions') {
      setQuestionsReady(true);
      const t = window.setTimeout(() => setQuestionsReady(false), 3000);
      return () => window.clearTimeout(t);
    }
    prevQuestionsLengthRef.current = questions.length;
  }, [questions.length, activeTab]);

  // Pulse the Questions tab when analysis finishes from another tab
  useEffect(() => {
    const keys = questions.filter((q) => q.vulnerabilities?.length || quickScanResults[q.id]).length;
    if (keys > 0 && prevAnalysisKeysRef.current === 0 && activeTab !== 'questions') {
      setQuestionsReady(true);
      const t = window.setTimeout(() => setQuestionsReady(false), 3000);
      return () => window.clearTimeout(t);
    }
    prevAnalysisKeysRef.current = keys;
  }, [questions, quickScanResults, activeTab]);

  // ─── Project mode ────────────────────────────────────────────────────────────

  if (isProject) {
    const stateLabel = formatProjectVersionState(detail?.state);
    const openReviewCount = reviewComments.filter(
      (c) => c.target_type !== 'comment' && c.status === 'suggested',
    ).length;
    return (
      <div className="container py-6 space-y-4">

        {/* Project header */}
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold tracking-tight">
            {projectInfo?.name || 'Project'}
          </h2>
          {projectId && (
            <span
              className="rounded border bg-muted/60 px-2 py-0.5 text-xs font-mono text-muted-foreground select-all cursor-pointer"
              title="Project ID — use this with MCP set_project"
              onClick={() => {
                navigator.clipboard.writeText(String(projectId));
                toast.success(`Project ID ${projectId} copied`);
              }}
            >
              ID: {projectId}
            </span>
          )}
          {stateLabel && (
            <span className="rounded border bg-muted/60 px-2 py-0.5 text-xs text-foreground shrink-0">
              {stateLabel}
            </span>
          )}
          {detail?.is_locked && (
            <span
              className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400 shrink-0"
              title={lockedWriteBackMessage(detail.state)}
            >
              <LockKeyhole className="h-3.5 w-3.5" />
              Locked
            </span>
          )}
          {threatCatUrl && (
            <a
              href={threatCatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Open in ThreatCat"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">ThreatCat</span>
            </a>
          )}
          {projectId ? (
            <>
              {isConnected ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 border border-green-500/20 px-2 py-0.5 text-xs text-green-600 dark:text-green-400 shrink-0">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                  Live
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Not connected</span>
              )}
              {detail?.content_synced_at && (
                <span className="text-xs text-muted-foreground">
                  Snapshot {new Date(detail.content_synced_at).toLocaleString()}
                </span>
              )}
              {syncState === 'syncing' && <span className="text-xs text-muted-foreground">Synchronizing...</span>}
              {syncState === 'error' && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-destructive">{syncError || 'Synchronization failed'}</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-destructive underline"
                    onClick={() => {
                      if (projectId && detail?.project_version_id) {
                        setPendingManualSync({ projectId, projectVersionId: detail.project_version_id });
                      }
                      setConnectOpen(true);
                    }}
                  >
                    Change credentials
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={synchronizeNow}>
                Synchronize now
              </Button>
              {(isConnected || Boolean(cookie)) && (
                <Button variant="ghost" size="sm" onClick={disconnect}>
                  Disconnect
                </Button>
              )}
            </>
          ) : (
            fileName && <span className="text-sm text-muted-foreground">{fileName}</span>
          )}
        </div>
        {detail?.is_locked && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
          >
            <LockKeyhole className="h-4 w-4 shrink-0" />
            <span>{lockedWriteBackMessage(detail.state)}</span>
          </div>
        )}

        {/* 5 project-level tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto overflow-y-hidden border-b mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="flex items-center bg-transparent p-0 w-max sm:w-auto">
              <TabsTrigger value="project-info" className={TRIGGER}>
                <Info className="h-4 w-4" />
                <span className="hidden sm:inline">Project Info</span>
              </TabsTrigger>
              <TabsTrigger value="notes" className={TRIGGER}>
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Notes</span>
              </TabsTrigger>
              <TabsTrigger value="diagrams" className={TRIGGER}>
                <Layers className="h-4 w-4" />
                <span className="hidden sm:inline">Diagrams{diagrams.length > 1 ? ` (${diagrams.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger
                value="questions"
                className={`${TRIGGER} ${questionsReady ? 'animate-pulse text-green-500' : ''}`}
                onClick={() => setQuestionsReady(false)}
              >
                <ClipboardList className="h-4 w-4" />
                <span className="hidden sm:inline">Questions{questions.length > 0 ? ` (${questions.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger value="threats" className={TRIGGER}>
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden sm:inline">Threats{threats.length > 0 ? ` (${threats.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger value="stride" className={TRIGGER}>
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">STRIDE</span>
              </TabsTrigger>
              <TabsTrigger value="recon" className={TRIGGER}>
                <FileSearch className="h-4 w-4" />
                <span className="hidden sm:inline">Recon</span>
              </TabsTrigger>
              <TabsTrigger value="review" className={TRIGGER}>
                <MessageSquare className="h-4 w-4" />
                <span className="hidden sm:inline">Review{openReviewCount > 0 ? ` (${openReviewCount})` : ''}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="project-info" className="space-y-4">
            <ProjectInfoTab />
          </TabsContent>
          <TabsContent value="notes" className="space-y-4">
            <NotesTab />
          </TabsContent>
          <TabsContent value="diagrams" className="space-y-4">
            <DiagramsTab
              view={diagramView}
              setView={setDiagramView}
              subTab={diagramSubTab}
              setSubTab={setDiagramSubTab}
            />
          </TabsContent>
          <TabsContent value="questions" className="space-y-4">
            <QuestionsTab />
          </TabsContent>
          <TabsContent value="threats" className="space-y-4">
            <ThreatsTab />
          </TabsContent>
          <TabsContent value="stride" className="space-y-4">
            <StrideTab />
          </TabsContent>
          <TabsContent value="recon" className="space-y-4">
            <ReconTab />
          </TabsContent>
          <TabsContent value="review" className="space-y-4">
            <ReviewTab
              onNavigateToDiagram={(_diagramIndex, subTab) => {
                setDiagramView('detail');
                setDiagramSubTab(subTab);
              }}
            />
          </TabsContent>
        </Tabs>
        <ConnectModal
          open={connectOpen}
          onClose={handleConnectClose}
          onConnected={handleConnected}
        />
      </div>
    );
  }

  // ─── Single-diagram mode ─────────────────────────────────────────────────────

  return (
    <div className="container py-6 space-y-4">

      {/* File header */}
      {fileName && (
        <p className="text-sm text-muted-foreground">{fileName}</p>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto overflow-y-hidden border-b mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="flex items-center bg-transparent p-0 w-max sm:w-auto">
            <TabsTrigger value="overview" className={TRIGGER}>
              <BarChart className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="connections" className={TRIGGER}>
              <GitBranch className="h-4 w-4" />
              <span className="hidden sm:inline">Connections</span>
            </TabsTrigger>
            <TabsTrigger value="entities" className={TRIGGER}>
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Entities</span>
            </TabsTrigger>
            <TabsTrigger value="boundaries" className={TRIGGER}>
              <Map className="h-4 w-4" />
              <span className="hidden sm:inline">Boundaries</span>
            </TabsTrigger>
            <TabsTrigger value="diagram" className={TRIGGER}>
              <Map className="h-4 w-4" />
              <span className="hidden sm:inline">Diagram</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className={TRIGGER}>
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Notes</span>
            </TabsTrigger>
            <TabsTrigger
              value="questions"
              className={`${TRIGGER} ${questionsReady ? 'animate-pulse text-green-500' : ''}`}
              onClick={() => setQuestionsReady(false)}
            >
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">Questions</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4"><OverviewTab /></TabsContent>
        <TabsContent value="connections" className="space-y-4"><ConnectionsTab /></TabsContent>
        <TabsContent value="entities" className="space-y-4"><EntitiesTab /></TabsContent>
        <TabsContent value="boundaries" className="space-y-4"><BoundariesTab /></TabsContent>
        <TabsContent value="diagram" className="space-y-4"><DiagramTab /></TabsContent>
        <TabsContent value="notes" className="space-y-4"><NotesTab /></TabsContent>
        <TabsContent value="questions" className="space-y-4"><QuestionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
